import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  LogOut,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
// Theme context removed - always dark mode
import '../../styles/user-profile.css';

export interface UserProfileProps {
  className?: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({ className = '', isOpen, onToggle }) => {
  const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Use external control if provided, otherwise use internal state
  const isDropdownOpen = isOpen !== undefined ? isOpen : internalDropdownOpen;
  const _setIsDropdownOpen = onToggle || ((open: boolean) => setInternalDropdownOpen(open));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        if (onToggle && isDropdownOpen) {
          onToggle();
        } else {
          setInternalDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, onToggle]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (onToggle && isDropdownOpen) {
        onToggle();
      } else {
        setInternalDropdownOpen(false);
      }
      buttonRef.current?.focus();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const closeDropdown = () => {
    if (onToggle && isDropdownOpen) {
      onToggle();
    } else {
      setInternalDropdownOpen(false);
    }
  };

  const menuItems = [
    {
      id: 'settings',
      label: 'Einstellungen',
      icon: Settings,
      action: () => {
        navigate('/settings');
        closeDropdown();
      },
    },
    {
      id: 'divider',
      isDivider: true,
    },
    {
      id: 'logout',
      label: 'Abmelden',
      icon: LogOut,
      action: handleLogout,
      variant: 'danger',
    },
  ];

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : 'User') return 'U';
    const names = `${user.first_name} ${user.last_name}`.split(' ');
    return names
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get avatar background color based on user name
  const getAvatarColor = () => {
    if (!user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : 'User') return '#6366f1';
    const colors = [
      '#3b82f6', // Blue
      '#8b5cf6', // Purple
      '#10b981', // Emerald
      '#f59e0b', // Amber
      '#ef4444', // Red
      '#ec4899', // Pink
      '#14b8a6', // Teal
    ];
    const index = `${user.first_name} ${user.last_name}`.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className={`user-profile ${className}`}>
      <button
        ref={buttonRef}
        className="user-profile__button"
        onClick={() => {
          if (onToggle) {
            onToggle();
          } else {
            setInternalDropdownOpen(!internalDropdownOpen);
          }
        }}
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
        aria-label="User menu"
      >
        <div
          className="user-profile__avatar"
          style={{
            background: user?.avatar_url
              ? `url(${user.avatar_url})`
              : `linear-gradient(135deg, ${getAvatarColor()}, ${getAvatarColor()}dd)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {!user?.avatar_url && (
            <span className="user-profile__initials">
              {getUserInitials()}
            </span>
          )}
        </div>

        {/* Online Status Indicator */}
        <div className="user-profile__status user-profile__status--online" />
      </button>

      <AnimatePresence>
        {isDropdownOpen && (
          <>
            {/* Backdrop for mobile */}
            <motion.div
              className="user-profile__backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => closeDropdown()}
            />

            {/* Dropdown Menu */}
            <motion.div
              ref={dropdownRef}
              className="user-profile__dropdown"
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              onKeyDown={handleKeyDown}
            >
              {/* User Info Header */}
              <div className="user-profile__header">
                <div
                  className="user-profile__header-avatar"
                  style={{
                    background: user?.avatar_url
                      ? `url(${user.avatar_url})`
                      : `linear-gradient(135deg, ${getAvatarColor()}, ${getAvatarColor()}dd)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {!user?.avatar_url && getUserInitials()}
                </div>
                <div className="user-profile__header-info">
                  <div className="user-profile__name">{user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : 'User'}</div>
                  <div className="user-profile__email">{user?.email || 'user@example.com'}</div>
                  <div className="user-profile__role">{user?.role || 'Team Member'}</div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="user-profile__menu">
                {menuItems.map((item) => {
                  if (item.isDivider) {
                    return <div key={item.id} className="user-profile__divider" />;
                  }

                  const Icon = item.icon!;
                  return (
                    <button
                      key={item.id}
                      className={`user-profile__menu-item ${
                        item.variant ? `user-profile__menu-item--${item.variant}` : ''
                      }`}
                      onClick={item.action}
                    >
                      <Icon className="user-profile__menu-icon" size={18} />
                      <span className="user-profile__menu-label">{item.label}</span>
                      {(item as any).badge && (
                        <span className="user-profile__menu-badge">{(item as any).badge}</span>
                      )}
                      <ChevronRight className="user-profile__menu-arrow" size={16} />
                    </button>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="user-profile__footer">
                <div className="user-profile__footer-text">
                  PHE Perm
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserProfile;